import { render, h, Fragment } from "preact";
import { useState, useEffect } from "preact/hooks";
import { locationService } from "../../api/client";
import useDataLayer from "../../util/UseDataLayer";
import useFormStore from "../../store/useFormStore";
import Alert from "../../components/Alert";

// TODO: Should not be able to add US zip code to canada and viceversa

const SERVICE_OPTIONS = [
  { value: "home", label: "Services for my home" },
  { value: "business", label: "Services for my business" },
  { value: "emergency", label: "Emergency Restoration" },
];

function BackButton({ handleBackClick }) {
  return (
    <button
      onClick={handleBackClick}
      className="flex items-center text-base text-coit-red cursor-pointer hover:text-coit-red-darker"
    >
      <svg
        className="w-4 h-4 mr-1"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 19l-7-7 7-7"
        />
      </svg>
      <span>Back</span>
    </button>
  );
}

export default function SetPostalCode({ country, onNext }) {
  const { pushToDataLayer } = useDataLayer();

  // Get state and actions from Zustand store
  const postalCode = useFormStore((state) => state.currentPostalCode);
  const setPostalCode = useFormStore((state) => state.setCurrentPostalCode);
  const franchise = useFormStore((state) => state.currentFranchise);
  const setFranchise = useFormStore((state) => state.setCurrentFranchise);
  const setIsTransitioning = useFormStore((state) => state.setIsTransitioning);
  const setFormStartTimestamp = useFormStore(
    (state) => state.setFormStartTimestamp,
  );
  const formExpiredMessage = useFormStore((state) => state.formExpiredMessage);
  const clearFormExpiredMessage = useFormStore(
    (state) => state.clearFormExpiredMessage,
  );

  const [status, setStatus] = useState(null);
  const [showExpirationAlert, setShowExpirationAlert] = useState(false);

  // Check for expiration message on mount
  useEffect(() => {
    if (formExpiredMessage) {
      setShowExpirationAlert(true);
    }
  }, [formExpiredMessage]);

  const [selectedService, setSelectedService] = useState("home");

  // Check if franchise is not accepting orders on mount
  useEffect(() => {
    if (franchise?.accepting_orders === false && selectedService === "home") {
      setStatus("not_accepting_orders");
    }
    if (
      franchise?.accepting_orders_commercial === false &&
      selectedService === "business"
    ) {
      setStatus("not_accepting_commercial_orders");
    }
  }, [franchise, selectedService]);

  const [validationState, setValidationState] = useState("idle"); // idle, validating, valid, invalid

  const handlePostalCodeValidation = async (code) => {
    if (!code.trim()) return;

    setValidationState("validating");

    try {
      // Use your existing API client
      const response = await locationService.validatePostalCode(code);
      setPostalCode(code);
      setFranchise(response.franchise);
      pushToDataLayer({
        event: "postal_code",
        serviceable: 1,
        postal_code: code,
      });
      // console.log('Valid postal code:', response);

      return response; // Return the full response object
    } catch (error) {
      // console.log('Invalid postal code:', error.message);
      // TODO: add datalayer event for invalid postal code
      pushToDataLayer({
        event: "postal_code",
        serviceable: 0,
        postal_code: code,
      });
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!postalCode.trim()) {
      alert("Please enter your postal code");
      return;
    }

    // Show loader during validation
    setIsTransitioning(true);

    try {
      // Validate postal code with API call
      const response = await handlePostalCodeValidation(postalCode);

      if (!response) {
        setIsTransitioning(false);
        setStatus("cannot-service");
        return;
      }

      // Handle emergency - stay on this screen
      if (selectedService === "emergency") {
        setIsTransitioning(false);
        setStatus("emergency");
        return;
      }

      // Check if franchise is accepting orders for commercial services
      if (selectedService === "business") {
        if (response.franchise?.accepting_orders_commercial === false) {
          setIsTransitioning(false);
          setStatus("not_accepting_commercial_orders");
          return;
        }
        // If accepting commercial orders, redirect to commercial form
        redirectBusiness();
        return;
      }

      // Check if franchise is accepting orders for residential services
      if (response.franchise?.accepting_orders === false) {
        setIsTransitioning(false);
        setStatus("not_accepting_orders");
        return;
      }

      // For home services, proceed to next screen
      // Set form start timestamp for 30-minute session timeout
      setFormStartTimestamp();
      // The loader will stay visible during transition
      onNext();
    } catch (error) {
      // console.error('Validation error:', error);
      setIsTransitioning(false);
    }
  };

  const onBack = () => {
    setStatus(null);
  };

  const handlePhoneCall = (phoneNumber) => {
    window.location.href = `tel:${phoneNumber}`;
  };

  const handleBackClick = () => {
    if (onBack) {
      onBack();
    } else {
      window.history.back();
    }
  };

  const redirectBusiness = () => {
    // Keep the loader visible during the redirect; the pageshow handler in App
    // clears it if the page is later restored (e.g. from bfcache) on back nav.
    window.location = "/request-quote";
  };

  const handleSwapLocationClick = () => {
    let domain = window.location.host;

    if (domain === "www.coit.com") {
      domain = "ca.coit.com";
    } else if (domain === "ca.coit.com") {
      domain = "www.coit.com";
    }
    // @todo: Add checking to see if it's a US or CA
    window.location = `https://${domain}/schedule-services`;
  };

  // Decide whether to use Canadian postal code rules or US ZIP rules.
  // Prefer a franchise hint if available; otherwise fall back to domain.
  // const isCanada = (franchise && (franchise.country === 'CA' || franchise.country_code === 'CA'))
  //   || (typeof window !== 'undefined' && /(^|\.)ca\.coit\.com$/i.test(window.location.host));
  const isCanada = country === "CA";

  // Format to US ZIP (##### or #####-####), stripping invalid characters.
  const formatUSZip = (value) => {
    const digits = (value || "").replace(/\D+/g, "").slice(0, 9);
    if (digits.length <= 5) return digits;
    return `${digits.slice(0, 5)}-${digits.slice(5)}`;
  };

  // Format to Canadian postal code (A1A 1A1). Uppercase and insert a space after 3 chars.
  const formatCAPostal = (value) => {
    const alnum = (value || "")
      .replace(/[^A-Za-z0-9]/g, "")
      .toUpperCase()
      .slice(0, 6);
    if (alnum.length <= 3) return alnum;
    return `${alnum.slice(0, 3)} ${alnum.slice(3)}`;
  };

  // Unified input change handler that enforces allowed characters as you type.
  const handlePostalInputChange = (e) => {
    const raw = e.target.value;
    const formatted = isCanada ? formatCAPostal(raw) : formatUSZip(raw);
    setPostalCode(formatted);
  };

  return (
    <div className="flex items-center justify-center p-4">
      <div className=" max-w-3xl w-full relative bg-white shadow-lg">
        {/* Form content */}
        <div className="p-8">
          {status === null && (
            <>
              {/* Session expiration alert */}
              {showExpirationAlert && formExpiredMessage && (
                <Alert
                  type="warning"
                  title="Session Expired"
                  onClose={() => {
                    setShowExpirationAlert(false);
                    clearFormExpiredMessage();
                  }}
                  leftAccent
                >
                  {formExpiredMessage}
                </Alert>
              )}

              <div class="mb-6 border-b border-b-gray-300 pb-4">
                <h1 class="text-3xl font-bold text-center">
                  Request COIT Cleaning Services
                </h1>
              </div>

              <form onSubmit={handleSubmit}>
                {/* Postal Code Section */}
                <div className="mb-8">
                  <label
                    htmlFor="postal-code-input"
                    className="block text-lg font-semibold mb-2"
                  >
                    Provide Postal Code{" "}
                    <span className="text-coit-error">*</span>
                  </label>
                  <input
                    id="postal-code-input"
                    type="text"
                    value={postalCode}
                    onChange={handlePostalInputChange}
                    inputMode={isCanada ? "text" : "numeric"}
                    autoComplete="postal-code"
                    maxLength={isCanada ? 7 : 10}
                    pattern={
                      isCanada
                        ? "^[A-Za-z]\\d[A-Za-z]( ?\\d[A-Za-z]\\d)?$"
                        : "^\\d{5}(-\\d{4})?$"
                    }
                    className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:outline-hidden focus:ring-2 focus:ring-coit-red focus:border-transparent text-lg"
                    placeholder={
                      isCanada
                        ? "Postal Code (e.g., V5K or V5K 1A2)"
                        : "ZIP Code (e.g., 94107 or 94107-1234)"
                    }
                    aria-describedby="postal-help"
                  />
                  <div id="postal-help" className={"description"}>
                    Providing the postal code for service will match you with
                    the location that services that area.
                  </div>
                </div>

                {/* Service Selection */}
                <fieldset className="mb-8 border-0 p-0">
                  <legend className="block text-lg font-semibold mb-2 p-0">
                    I am interested in <span className="text-coit-red">*</span>
                  </legend>

                  <div className="space-y-4">
                    {SERVICE_OPTIONS.map(({ value, label }) => (
                      <label
                        key={value}
                        className="flex items-center min-h-11 cursor-pointer"
                      >
                        <input
                          type="radio"
                          name="selectedService"
                          value={value}
                          checked={selectedService === value}
                          onChange={() => setSelectedService(value)}
                          className="peer sr-only"
                        />
                        <div
                          className={`w-6 h-6 shrink-0 rounded-full border-2 flex items-center justify-center peer-focus-visible:ring-2 peer-focus-visible:ring-black peer-focus-visible:ring-offset-2 ${
                            selectedService === value
                              ? "border-coit-red bg-coit-red"
                              : "border-gray-500 bg-white"
                          }`}
                        >
                          {selectedService === value && (
                            <div className="w-3 h-3 rounded-full bg-white"></div>
                          )}
                        </div>
                        <span className="ml-4 text-lg text-gray-700">
                          {label}
                        </span>
                      </label>
                    ))}
                  </div>
                </fieldset>

                {/* Continue Button */}
                <div className="flex flex-col lg:flex-row justify-between items-center gap-4">
                  <div>
                    <a
                      href="/"
                      className="text-coit-red hover:text-coit-red-darker focus:text-coit-red-darker"
                    >
                      Take me to the homepage
                    </a>
                  </div>
                  <button
                    type="submit"
                    className="coit-button w-full lg:w-auto"
                  >
                    Let's Get Started
                  </button>
                </div>
              </form>
            </>
          )}

          {status === "cannot-service" && (
            <>
              <BackButton handleBackClick={handleBackClick} />

              {/* Main Content */}
              <div className="text-center">
                <h1 className="text-4xl font-normal text-gray-800 mb-6">
                  Oh No!
                </h1>

                <h2 className="text-2xl font-normal text-gray-800 mb-6">
                  It appears your zip code is not in a serviceable area.
                </h2>

                <p className="text-lg text-gray-600 mb-12">
                  Please call us at{" "}
                  <a
                    href="tel:1-800-367-2648"
                    className="text-coit-red hover:underline"
                  >
                    1-800-367-2648
                  </a>{" "}
                  for more information on service areas and availability.
                </p>

                {/* Canada Notice */}
                <div className="border-t border-gray-200 pt-8">
                  <p className="text-lg text-gray-800 mb-6">
                    <span className="font-semibold">Canada Residents</span> -
                    You have somehow reached our COIT US site.
                  </p>

                  <button
                    onClick={handleSwapLocationClick}
                    className="coit-button"
                  >
                    Visit Canada Website
                  </button>

                  <p className="text-sm text-gray-600 mt-4">
                    Search for your serviceable areas on our Canada site.
                  </p>
                </div>
              </div>
            </>
          )}

          {status === "emergency" && (
            <>
              <BackButton handleBackClick={handleBackClick} />

              {/* Main Content */}
              <div className="text-center">
                <div className="text-4xl font-normal text-gray-800 mb-6">
                  Emergency Restoration
                </div>

                <p className="text-lg text-gray-600 mb-12 max-w-2xl mx-auto">
                  Call us to schedule your emergency restoration service
                  appointment.
                </p>

                {/* Phone Button */}
                <button
                  onClick={() => {
                    handlePhoneCall(franchise?.phone?.restoration_phone_number);
                  }}
                  className="coit-button flex mx-auto items-center"
                >
                  <svg
                    className="w-6 h-6 mr-3"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                  </svg>
                  <span>{franchise?.phone?.restoration_phone_number}</span>
                </button>
              </div>
            </>
          )}

          {status === "not_accepting_orders" && (
            <>
              <BackButton handleBackClick={handleBackClick} />

              {/* Main Content Not Accepting Orders */}
              <div className="text-center max-w-3xl mx-auto mt-2">
                {franchise?.not_accepting_online_request_message ? (
                  <div className="text-3xl font-normal text-gray-800 mb-6 leading-relaxed [&_p]:leading-relaxed [&_p:last-child]:mb-0">
                    <div
                      dangerouslySetInnerHTML={{
                        __html: franchise.not_accepting_online_request_message,
                      }}
                    />
                  </div>
                ) : (
                  <div className="text-3xl font-normal text-gray-800 mb-6 leading-relaxed">
                    We are currently not accepting online scheduling. Call{" "}
                    <a
                      href={`tel:${franchise?.phone?.telephone || "1-800-367-2648"}`}
                    >
                      {franchise?.phone?.telephone || "1-800-367-2648"}
                    </a>
                  </div>
                )}
              </div>
            </>
          )}

          {status === "not_accepting_commercial_orders" && (
            <>
              <BackButton handleBackClick={handleBackClick} />

              {/* Main Content Not Accepting Commercial Orders */}
              <div className="text-center max-w-3xl mx-auto mt-2">
                {franchise?.not_accepting_commercial_online_request_message ? (
                  <div className="text-3xl font-normal text-gray-800 mb-6 leading-relaxed [&_p]:leading-relaxed [&_p:last-child]:mb-0">
                    <div
                      dangerouslySetInnerHTML={{
                        __html:
                          franchise.not_accepting_commercial_online_request_message,
                      }}
                    />
                  </div>
                ) : (
                  <div className="text-3xl font-normal text-gray-800 mb-6 leading-relaxed">
                    We are currently not accepting commercial online scheduling.
                    Call{" "}
                    <a
                      href={`tel:${franchise?.phone?.telephone || "1-800-367-2648"}`}
                    >
                      {franchise?.phone?.telephone || "1-800-367-2648"}
                    </a>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
